/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   helper functions into determining a value in state.
 */

import { createSelector, memoizeMaybeRouteProps, memoizeRouteProps } from '@zcode/memoizers'
import { type ZumperRoute, matchPath } from '@zcode/router-utils'
import type { State } from '@zcode/state-type'
import {
  currentSearchRoutes,
  detailRoutes,
  maplistRoutes,
  permanentAddressRoutes,
  routesConstants,
} from '@zcode/zumper-routes'
import type { StaticContext } from 'react-router'
import type { RouteChildrenProps, RouteComponentProps } from 'react-router-dom'

import { SEARCH_VIEW_TYPE } from './constants'

export const getCurrentMinCities = (state: State) => {
  return state.currentSearch.minCities.minCities
}

export function getCurrentMinCity(
  state: State
): ReturnType<typeof getCurrentMinCities>[0] | undefined {
  const cities = getCurrentMinCities(state)
  return cities[0]
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export function getCurrentCity(state: State) {
  return state.currentSearch && (state.currentSearch.city || getCurrentMinCity(state))
}

export const checkMainView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  if (!location) {
    return false
  }
  const { pathname } = location

  return (
    !pathname?.startsWith(routesConstants.NZR_FAVORITES) &&
    !pathname?.startsWith(routesConstants.NZR_ALERTS) &&
    !pathname?.startsWith(routesConstants.NZR_MESSAGED)
  )
})

export const checkDetailView = memoizeRouteProps((routeProps: RouteChildrenProps) => {
  const { location } = routeProps
  return !!(
    location?.pathname &&
    detailRoutes.some(
      (route: ZumperRoute) => location.pathname && matchPath(location.pathname, route)
    )
  )
})

export const checkPermanentAddressView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(
    location?.pathname &&
    permanentAddressRoutes.some((route) => location.pathname && matchPath(location.pathname, route))
  )
})

export const selectIsRailPage = memoizeRouteProps((routeProps: RouteComponentProps) => {
  const { location } = routeProps
  return !!(
    location?.pathname &&
    maplistRoutes.some((route) => location.pathname && matchPath(location.pathname, route)) &&
    !checkDetailView(routeProps)
  )
})

export const checkHomepageView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(location && location.pathname === routesConstants.NZR_HOMEPAGE)
})

/**
 * returns current listables `matching` count
 *
 * @param state
 */
export const getMatchingCount = (state: State) => {
  return state.currentSearch.listables.matching
}

export const getCurrentCityPath = createSelector(getCurrentCity, (currentCity) => {
  return currentCity?.path
})

export function getCurrentLocation(state: State) {
  return state.currentSearch.location
}

export const getHoveredListable = (state: State) => {
  return state.searchUI.hoveredItem
}

export const getCheckVisited =
  (state: State) =>
  ({
    uId,
    buildingId,
    listingId,
    paUrl,
  }: {
    uId?: string
    buildingId?: number | null
    listingId?: number | null
    paUrl?: string | null
  }): boolean => {
    if (!uId && !buildingId && !listingId && !paUrl) {
      return false
    }
    const { visited } = state.bundle
    return !!(
      (uId && uId in visited) ||
      (buildingId && buildingId in visited) ||
      (listingId && listingId in visited) ||
      (paUrl && paUrl in visited)
    )
  }

export const selectSelectedPOICategory = (state: State) =>
  state?.currentSearchView?.selectedPOICategory

export const selectIsCurrentSearchView = memoizeRouteProps(
  (routeProps: RouteComponentProps<{}, StaticContext, any>) => {
    const { location } = routeProps
    return !!(
      location?.pathname &&
      currentSearchRoutes.some((route: ZumperRoute) => matchPath(location.pathname, route)) &&
      !checkDetailView(routeProps)
    )
  }
)
